import React, { useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';

import { commaFormatNumber } from '@Utils/String';
import { mapContextToTitle } from '@Containers/ResultToolbar/ResultToolbarConstants';
import { MetadataPropTypesShape } from '@Components/Results/MetadataPropTypes';

import Page from '@Containers/Page';
import BreadcrumbsContainer from '@Containers/Breadcrumbs';
import ResultsFilters from '@Containers/ResultsFilters';
import ResultToolbarContainer from '@Containers/ResultToolbar';
import DatabaseSwitcherTabs from '@Containers/ResultToolbar/DatabaseSwitcher';
import ResultTopPanelContainer from '@Containers/ResultToolbar/ResultTopPanel/ResultTopPanelContainer';
import SummaryContainer from '@Containers/Summary/SummaryContainer';

import ResultsContentSection from './Components/ResultsContentSection';

import './ResultsPage.scss';

export default function ResultsPage({
  stateParamsReady,
  isGridView,
  toggleGridView,
  availableDatabases,
  currentDatabase,
  isDbSwitcherVisible,
  selectDatabase,
  goToResultPage,
  supportsReactions,
  showAllPreparations,
  metadata,
  context,
  results,
  searchType,
  originalSearchType,
  currentPageSize,
  resultsLoading,
  paginationVisible,
  showConditions,
  toggleConditions,
  recordCountOrTotalResult,
  isLimitDisabled,
  limitTo,
  isExportEnabled,
  openExportDialog,
  params,
  activeBreadcrumb,

  // Selection
  isAllResultsChecked,
  isAllResultsCheckedPartial,
  setAllResultsChecked,
  setAllResultsUnchecked,
  checkedItemsNumber,
  currentPage,
  totalPages,
}) {
  const resultsWrapperAttrs = {
    id: `results-tabpanel-${context}-${metadata.databaseId}`,
    role: 'tabpanel',
    'aria-labelledby': `databases-tab-${context}-${metadata.databaseId}`,
  };

  const scrollableResultsContainerRef = useRef(null);
  const resetResultsScrollTop = useCallback(() => {
    if (scrollableResultsContainerRef.current) {
      scrollableResultsContainerRef.current.scrollTop = 0;
    }
  }, []);

  return (
    <Page id="results-page" className={cn('results-page-container', { 'grid-tiles-view': isGridView })} withFooter={false}>
      <BreadcrumbsContainer />
      <DatabaseSwitcherTabs
        className="u-els-margin-left-2x u-els-margin-right-2x u-els-margin-top u-els-margin-bottom"
        availableDatabases={availableDatabases}
        isDbSwitcherVisible={isDbSwitcherVisible}
        currentDatabase={currentDatabase}
        selectDatabase={selectDatabase}
      />
      <ResultTopPanelContainer goToResultPage={goToResultPage} params={params} />
      <div className={`flex-item-height-fix padded-container ${context}-results`}>
        <ResultsFilters className="o-els-flex-layout__item" />
        <section
          ref={scrollableResultsContainerRef}
          id="results-container"
          className="results-container results-container--substance-result-optimization o-els-flex-layout__item o-els-flex-layout__item--grow"
          aria-label={`Results: ${commaFormatNumber(recordCountOrTotalResult || '')} ${mapContextToTitle[context]}`}
          aria-busy={resultsLoading}>
          <div className="results-wrapper" {...resultsWrapperAttrs}>
            <div className="results-list-wrapper-inner">
              <SummaryContainer
                activeBreadcrumb={activeBreadcrumb}
                params={params}
                metadata={metadata}
                resetResultsScrollTop={resetResultsScrollTop}
              />
              {stateParamsReady && (
                <ResultToolbarContainer
                  isListView={!isGridView}
                  togglerResultView={toggleGridView}
                  availableDatabases={availableDatabases}
                  isDbSwitcherVisible={isDbSwitcherVisible}
                  currentDatabase={currentDatabase}
                  selectDatabase={selectDatabase}
                  goToResultPage={goToResultPage}
                  supportsReactions={supportsReactions}
                  showAllPreparations={showAllPreparations}
                  showConditions={showConditions}
                  toggleConditions={toggleConditions}
                  checkAllItems={setAllResultsChecked}
                  uncheckAllItems={setAllResultsUnchecked}
                  allItemsSelected={isAllResultsChecked}
                  someItemsSelected={isAllResultsCheckedPartial}
                  currentSelectedItems={checkedItemsNumber}
                  disableLimit={isLimitDisabled}
                  limitTo={limitTo}
                  isExportEnabled={isExportEnabled}
                  openExportDialog={openExportDialog}
                />
              )}

              <ResultsContentSection
                metadata={metadata}
                context={context}
                results={results}
                gridView={isGridView}
                searchType={searchType}
                originalSearchType={originalSearchType}
                currentPageSize={currentPageSize}
                currentPage={currentPage}
                totalPages={totalPages}
                resultsLoading={resultsLoading}
                paginationVisible={paginationVisible}
                activeBreadcrumb={activeBreadcrumb}
              />
            </div>
          </div>
        </section>
      </div>
    </Page>
  );
}

ResultsPage.propTypes = {
  stateParamsReady: PropTypes.bool,
  isGridView: PropTypes.bool,
  toggleGridView: PropTypes.func.isRequired,
  availableDatabases: PropTypes.array,
  currentDatabase: PropTypes.object,
  isDbSwitcherVisible: PropTypes.bool,
  selectDatabase: PropTypes.func.isRequired,
  goToResultPage: PropTypes.func.isRequired,
  supportsReactions: PropTypes.bool,
  showAllPreparations: PropTypes.func.isRequired,
  metadata: MetadataPropTypesShape,
  context: PropTypes.string,
  results: PropTypes.array.isRequired,
  searchType: PropTypes.string,
  originalSearchType: PropTypes.string,
  currentPageSize: PropTypes.number,
  resultsLoading: PropTypes.bool,
  paginationVisible: PropTypes.bool,
  showConditions: PropTypes.bool,
  toggleConditions: PropTypes.func.isRequired,
  recordCountOrTotalResult: PropTypes.number,
  isLimitDisabled: PropTypes.bool,
  limitTo: PropTypes.func.isRequired,
  isExportEnabled: PropTypes.bool,
  openExportDialog: PropTypes.func.isRequired,

  // Selection
  isAllResultsChecked: PropTypes.bool,
  isAllResultsCheckedPartial: PropTypes.bool,
  setAllResultsChecked: PropTypes.func.isRequired,
  setAllResultsUnchecked: PropTypes.func.isRequired,
  checkedItemsNumber: PropTypes.number,
  currentPage: PropTypes.number,
  totalPages: PropTypes.number,
};
